<template>
  <div class="file-uploader">
    <slot :click-handler="onFileButtonClick" name="default" />
    <div v-if="!slots.default" :class="attrsClass">
      <FileCount
        :hide-file-count="!showFileCount"
        v-model="fileCollection"
        :is-uploading="isUploading || overrideIsUploading"
        :class="iconsClass"
        :preview-class="previewClass"
        :on-delete="onDelete"
        :on-move="onMove"
      >
        <template #prepend v-if="!disabled">
          <GeneralButton
            title="Bestand uploaden"
            color="surface"
            class="bg-transparent p-0! text-current!"
            @click.stop.prevent="onFileButtonClick"
            :icon="['fas', 'paperclip']"
            :tabindex="tabindex"
          />
        </template>
        <template #default v-if="$slots['file-count']">
          <slot name="file-count" />
        </template>
      </FileCount>
    </div>

    <input
      ref="fileInput"
      name="file-uploader"
      hidden
      :multiple="multiple"
      type="file"
      :capture="capture"
      :accept="accept"
      @change="onFileSelect"
      @cancel="$emit('cancel')"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  type Ref,
  type Slots,
  useAttrs,
  useSlots,
  watch,
  type HTMLAttributes
} from "vue"
import { useFileUpload } from "@/composables/file/file-upload"
import type { FileRelation } from "@/models/file/file-relation.model"
import FileCount from "@/components/file/FileCount.vue"
import type { ShowFileViewerPropOptions } from "@/components/file/FileCount.vue"
import { FileUploadContexts } from "@/enums/file-upload-contexts"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import { FileBucket } from "@/models/file-bucket.model"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"

const props = withDefaults(
  defineProps<{
    /**
     * The context in which the file uploader is used.
     */
    context: FileUploadContexts

    /**
     * Prevent uploading
     */
    disabled?: boolean

    /**
     * Whether the file uploader should be standalone or not. If true, uploaded files will not be attached to a model.
     */
    standalone?: boolean

    /**
     * The model to which the files will be attached.
     */
    fileBucket?: FileBucket

    /**
     * Position the icons in a different position (for example when placed in a textarea).
     */
    iconsClass?: string

    /**
     * Whether to show the file count. This file count is clickable and will open the file viewer.
     */
    showFileCount?: boolean

    /**
     * If using a file count, when to open the preview dialog. If a number, the dialog will open when the file count is
     * equal to or greater than that number. Else (or if never), the FileViewer will be opened immediately.
     */
    previewCondition?: ShowFileViewerPropOptions

    /**
     * If standalone is true, this prop can be used to pass a collection of file relations.
     * These are used to display the file count and open the file viewer.
     */
    standaloneFileRelationCollection?: FileRelation[]

    /**
     * Capture attribute for file input element.
     */
    capture?: "user" | "environment"

    /**
     * Accept attribute for file input element.
     */
    accept?: string

    /**
     * Whether to manage the uploaded files using ModelManager (see ModelManager for more info).
     */
    managed?: boolean

    /**
     * Whether to allow multiple files to be uploaded.
     */
    multiple?: boolean

    /**
     * Allows to override the isUploading state. This is useful when the uploader is used in a context where other
     * components are uploading files and the isUploading state should be shared.
     */
    overrideIsUploading?: boolean

    /**
     * CSS class to control the file preview alignment (e.g., 'justify-start' for left alignment)
     */
    previewClass?: string

    tabindex?: string | undefined

    onDelete?: (fileRelation: FileRelation) => void

    onMove?: (fileRelation: FileRelation) => void
  }>(),
  {
    standalone: false,
    iconsClass: "",
    showFileCount: false,
    previewCondition: 2,
    standaloneFileRelationCollection: () => [],
    accept: "",
    managed: true,
    multiple: true,
    overrideIsUploading: false,
    previewClass: "",
    tabindex: "-1"
  }
)

const emit = defineEmits(["isUploading", "fileSelect", "fileUploaded", "click", "cancel"])

const fileInput: Ref<HTMLInputElement | null> = ref(null)

const attrs = useAttrs()
const attrsClass = computed(() => attrs.class as HTMLAttributes["class"])

const slots: Slots = useSlots()

const { upload, uploadAndCreateRelation, isUploading } = useFileUpload(props.managed, {
  accept: props.accept
})

const fileCollection = computed(() => {
  if (props.standalone) {
    return props.standaloneFileRelationCollection
  } else if (props.fileBucket!.fileRelations) {
    return props.fileBucket!.fileRelations
  } else return []
})

/**
 * Checks if the files are duplicates.
 * @param files - The files to check.
 * @returns True if the files are duplicates, false otherwise.
 */
async function filesAreDuplicates(files: File[]): Promise<boolean> {
  const existingHashes = new Set<string>()
  // Hash all existing files and put their hex-digests into a Set
  await Promise.all(
    fileCollection.value.map(async (relation) => {
      const resp = await fetch(relation.file.url)
      const blob = await resp.blob()
      const buffer = await blob.arrayBuffer()
      const hashBuffer = await crypto.subtle.digest("SHA-256", buffer)
      // Convert to hex
      const hex = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
      existingHashes.add(hex)
    })
  )

  // Hash each new file, as soon as one matches, return true
  for (const file of files) {
    const buffer = await file.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer)
    const hex = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
    if (existingHashes.has(hex)) {
      return true
    }
  }

  return false
}

/**
 * Handles the file selection event.
 * @param event - The event object.
 */
async function onFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  if (!target?.files?.length) {
    return
  }

  if (await filesAreDuplicates(Array.from(target.files))) {
    const body =
      target.files.length === 1
        ? "Dit bestand is hier al aan toegevoegd."
        : "Een of meer van de geüploade bestanden zijn hier al aan toegevoegd."
    useDialogStore().currentDialog = {
      title: "Bestand bestaat al",
      body,
      type: DialogTypes.OK,
      mandatedWaitTimeSec: 0
    }
    target.value = ""
    return
  }

  emit("fileSelect", target.files)
  if (props.standalone) {
    await upload(Array.from(target.files), props.context, onFileUploaded)
  } else {
    if (!props.fileBucket) {
      throw new Error("No fileBucket prop passed and standalone mode is not enabled.")
    }
    await uploadAndCreateRelation(
      Array.from(target.files),
      props.fileBucket,
      props.context,
      onFileUploaded
    )
  }
  target.value = ""
}

function onFileUploaded(fileRelation: FileRelation) {
  emit("fileUploaded", fileRelation)
}

function onFileButtonClick(e: Event) {
  fileInput.value!.click()
  emit("click", e)
}

watch(isUploading, (val) => {
  emit("isUploading", val)
})

/** Open the native file picker programmatically (e.g. from the "  +" shortcut). */
defineExpose({ open: () => fileInput.value?.click() })
</script>

<style scoped></style>
